import React, { useState, useMemo } from 'react';

import {
  ArrowLeftOutlined,
  SaveOutlined,
  SettingOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import {
  Button,
  Tabs,
  Form,
  Input,
  Select,
  Tag,
  Typography,
  Drawer,
  Space,
  Tooltip,
  message,
} from 'antd';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import CampaignsTab from './components/campaigns/CampaignsTab';
import AdsTab from './components/ads/AdsTab';
import OffersTab from './components/OffersTab';
import SemanticMapTab, { mockSmOffers, allConnectedCabinets, getProductSpheres } from './components/SemanticMapTab';
import { useDb } from './db/useDataService';
import './ProductPage.scss';
import {
  Product,
  ProductStatus,
  ProductAuthor,
  ProductTag,
  SmOffer,
  AdCabinet,
  LifeSphere,
} from './types';
import MainLayout from '../../shared/common/components/layouts/MainLayout';

const { Title } = Typography;
const { TextArea } = Input;
const { TabPane } = Tabs;

// --- Mock data ---

const mockAuthors: ProductAuthor[] = [
  { id: '1', name: 'Иван Петров', description: 'Эксперт по маркетингу' },
  { id: '2', name: 'Мария Сидорова', description: 'Бизнес-тренер' },
  { id: '3', name: 'Алексей Козлов', description: 'Специалист по продажам' },
];

const mockTags: ProductTag[] = [
  { id: '1', name: 'Основной', color: 'blue', categoryId: '1', categoryName: 'Тип' },
  { id: '2', name: 'Допродажа', color: 'green', categoryId: '1', categoryName: 'Тип' },
  { id: '3', name: 'Тест', color: 'orange', categoryId: '2', categoryName: 'Статус работы' },
  { id: '4', name: 'Масштаб', color: 'purple', categoryId: '2', categoryName: 'Статус работы' },
  { id: '5', name: 'Важное', color: 'red' },
];

const mockProducts: Record<string, Product> = {
  '1': {
    id: '1',
    name: 'Курс «Основы маркетинга»',
    url: 'https://example.com/marketing',
    description: 'Комплексный курс по основам маркетинга для начинающих',
    tasks: 'Обучение основам маркетинга',
    keyIdea: 'Системный подход к маркетингу с нуля',
    keyMeanings: 'Практика, результат, система',
    excludeWords: 'бесплатно, халява',
    keywords: 'маркетинг, продвижение, реклама',
    notes: 'Запуск планируется в апреле',
    comment: 'Основной продукт школы',
    status: 'active',
    authorId: '1',
    author: mockAuthors[0],
    tags: [mockTags[0], mockTags[3]],
    offersCount: 3,
    createdAt: '2026-01-15T10:00:00Z',
    createdBy: 'user1',
  },
  '2': {
    id: '2',
    name: 'Вебинар «Воронки продаж»',
    url: 'https://example.com/funnels-webinar',
    description: 'Бесплатный вебинар о построении воронок продаж',
    tasks: 'Привлечение лидов через вебинар',
    keyIdea: 'Автоматизация продаж через воронки',
    keyMeanings: 'Автоматизация, конверсия, доход',
    excludeWords: '',
    keywords: 'воронка, продажи, автоматизация',
    notes: 'Запуск в марте',
    comment: '',
    status: 'draft',
    authorId: '2',
    author: mockAuthors[1],
    tags: [mockTags[2]],
    offersCount: 1,
    createdAt: '2026-02-20T14:30:00Z',
    createdBy: 'user1',
  },
  '10': {
    id: '10',
    name: 'Курс «Энергия мысли. Открой себя»',
    url: 'https://donskoj.info/otkrojsebja-online',
    description: 'Трансформационный online-курс Анатолия Донского. 21 день практики: перепрограммирование мышления, оздоровление, медитации',
    tasks: 'Привлечение через бесплатный вебинар, продажа курса',
    keyIdea: 'Мысль определяет здоровье, отношения и доход — научитесь управлять ей',
    keyMeanings: 'Сила мысли, оздоровление, трансформация, альфа-состояние, медитации',
    excludeWords: '',
    keywords: 'энергия мысли, Донской, оздоровление, медитация, подсознание',
    notes: '',
    comment: '',
    status: 'active',
    authorId: null,
    author: null,
    tags: [],
    offersCount: 2,
    createdAt: '2026-04-01T10:00:00Z',
    createdBy: 'user1',
  },
};

const STATUS_CONFIG: Record<ProductStatus, { label: string; color: string }> = {
  draft: { label: 'Черновик', color: 'default' },
  active: { label: 'Запущено', color: 'green' },
  stopped: { label: 'Остановлено', color: 'orange' },
  archived: { label: 'Архив', color: 'red' },
};

// --- Component ---

const ProductPage: React.FC = () => {
  const db = useDb();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const product = useMemo(() => {
    return id ? mockProducts[id] : null;
  }, [id]);

  const [form] = Form.useForm();
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [settingsTab, setSettingsTab] = useState('general');
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'semantic';
  const SEMANTIC_PARAMS = ['sphere', 'category', 'theme', 'unit', 'offer', 'platform', 'cabinet', 'angle'];
  const ADS_PARAMS = ['adPlatform', 'adCabinet', 'adCampaign', 'adGroup', 'adLevel'];
  const handleChangeTab = (key: string) => {
    const params = new URLSearchParams(searchParams);
    if (key === 'semantic') params.delete('tab');
    else params.set('tab', key);
    // Чистим параметры других вкладок, чтобы URL отражал реальный контекст
    if (key !== 'semantic') SEMANTIC_PARAMS.forEach((p) => params.delete(p));
    if (key !== 'ads') ADS_PARAMS.forEach((p) => params.delete(p));
    setSearchParams(params, { replace: true });
  };

  if (!product) {
    return (
      <MainLayout pageTitle="Продукт не найден">
        <div className="page-wrapper">
          <div className="page-content">
            <div className="product-page">
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate('/products')}
                style={{ marginBottom: 16 }}
              >
                Назад к продуктам
              </Button>
              <Title level={4}>Продукт не найден</Title>
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  const handleSave = () => {
    form.validateFields().then(() => {
      message.success('Продукт сохранён');
    });
  };

  return (
    <MainLayout pageTitle={product.name}>
      <div className="page-wrapper">
        <div className="page-content">
          <div className="product-page">
            <div className="product-page__header">
              <div className="product-page__header-left">
                <Button
                  type="text"
                  icon={<ArrowLeftOutlined />}
                  onClick={() => navigate('/products')}
                  className="product-page__back-btn"
                />
                <Title level={4} className="product-page__title">
                  {product.name}
                </Title>
                <Tag color={STATUS_CONFIG[product.status].color}>
                  {STATUS_CONFIG[product.status].label}
                </Tag>
              </div>
              <Button
                type="text"
                icon={<SettingOutlined />}
                className="product-page__settings-btn"
                onClick={() => setDrawerVisible(true)}
              />
            </div>

            <Tabs activeKey={activeTab} onChange={handleChangeTab} className="product-page__tabs">
              <TabPane tab="Карта смыслов" key="semantic">
                <SemanticMapTab productId={product.id} />
              </TabPane>
              <TabPane tab="Реклама" key="ads">
                <AdsTab
                  productId={product.id}
                  cabinets={db.adCabinets}
                  offers={db.offers.filter((o) => o.productId === product.id) as SmOffer[]}
                  spheres={getProductSpheres(product.id)}
                />
              </TabPane>
              <TabPane tab="Офферы" key="offers">
                <OffersTab productId={product.id} productName={product.name} spheres={getProductSpheres(product.id)} />
              </TabPane>
            </Tabs>

            <Drawer
              title="Настройки продукта"
              placement="right"
              width={720}
              visible={drawerVisible}
              onClose={() => setDrawerVisible(false)}
              footer={
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                  <Button onClick={() => setDrawerVisible(false)}>
                    Закрыть
                  </Button>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={handleSave}
                    style={{
                      backgroundColor: '#ff4d4f',
                      borderColor: '#ff4d4f',
                    }}
                  >
                    Сохранить
                  </Button>
                </div>
              }
            >
              <Form
                form={form}
                layout="vertical"
                initialValues={{
                  name: product.name,
                  url: product.url,
                  description: product.description,
                  tasks: product.tasks,
                  keyIdea: product.keyIdea,
                  keyMeanings: product.keyMeanings,
                  excludeWords: product.excludeWords,
                  keywords: product.keywords,
                  notes: product.notes,
                  comment: product.comment,
                  authorId: product.authorId,
                  status: product.status,
                  tagIds: product.tags.map((t) => t.id),
                }}
                requiredMark={false}
              >
                <Tabs
                  activeKey={settingsTab}
                  onChange={setSettingsTab}
                >
                  <TabPane tab="Основное" key="general">
                    <div style={{ paddingTop: 16 }}>
                      <Form.Item
                        name="name"
                        label="Название продукта"
                        rules={[{ required: true, message: 'Введите название' }]}
                      >
                        <Input placeholder="Название продукта" />
                      </Form.Item>
                      <Form.Item
                        name="url"
                        label={
                          <Space>
                            Ссылка на продукт
                            <Tooltip title="Спарсить страницу и автоматически построить карту смыслов">
                              <Button
                                size="small"
                                type="link"
                                icon={<ThunderboltOutlined />}
                                onClick={() => {
                                  const url = form.getFieldValue('url');
                                  if (!url) { message.warning('Сначала укажите URL продукта'); return; }
                                  message.loading({ content: 'Парсим страницу…', key: 'parse', duration: 0 });
                                  // TODO: реальный вызов бэкенда парсинга. Пока — заглушка.
                                  setTimeout(() => {
                                    message.success({ content: 'Парсинг завершён. Карта смыслов будет обновлена бэкендом (заглушка).', key: 'parse' });
                                  }, 1200);
                                }}
                              >Спарсить страницу</Button>
                            </Tooltip>
                          </Space>
                        }
                        rules={[{ type: 'url', message: 'Введите корректный URL' }]}
                      >
                        <Input placeholder="https://example.com/product" />
                      </Form.Item>
                      <Form.Item
                        name="description"
                        label="Описание продукта"
                      >
                        <TextArea rows={3} placeholder="Подробное описание продукта" />
                      </Form.Item>
                      <Form.Item
                        name="tasks"
                        label="Задачи продукта"
                      >
                        <TextArea rows={2} placeholder="Какие задачи решает продукт" />
                      </Form.Item>
                      <Form.Item
                        name="keyIdea"
                        label="Ключевая мысль"
                      >
                        <TextArea
                          rows={2}
                          placeholder="Уникальность продукта. За счёт чего достигается результат?"
                        />
                      </Form.Item>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                        <Form.Item
                          name="authorId"
                          label="Автор"
                        >
                          <Select placeholder="Выберите автора" allowClear>
                            {mockAuthors.map((author) => (
                              <Select.Option key={author.id} value={author.id}>
                                {author.name}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                        <Form.Item
                          name="status"
                          label="Статус"
                        >
                          <Select>
                            {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                              <Select.Option key={key} value={key}>
                                {config.label}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </div>
                      <Form.Item
                        name="tagIds"
                        label="Теги"
                      >
                        <Select
                          mode="multiple"
                          placeholder="Выберите теги"
                          allowClear
                        >
                          {mockTags.map((tag) => (
                            <Select.Option key={tag.id} value={tag.id}>
                              {tag.name}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </div>
                  </TabPane>

                  <TabPane tab={`Офферы (${product.offersCount})`} key="offers">
                    <OffersTab productId={product.id} productName={product.name} spheres={getProductSpheres(product.id)} />
                  </TabPane>

                  <TabPane tab="Настройки" key="settings">
                    <div style={{ paddingTop: 16 }}>
                      <Form.Item
                        name="keyMeanings"
                        label="Ключевые смыслы и триггеры"
                      >
                        <TextArea
                          rows={3}
                          placeholder="Основные смыслы и триггеры продукта"
                        />
                      </Form.Item>
                      <Form.Item
                        name="excludeWords"
                        label="Слова-исключения"
                      >
                        <TextArea
                          rows={2}
                          placeholder="Слова, которые не нужно использовать в рекламе (через запятую)"
                        />
                      </Form.Item>
                      <Form.Item
                        name="keywords"
                        label="Ключевые слова"
                      >
                        <TextArea
                          rows={2}
                          placeholder="Слова, которые характеризуют продукт (через запятую)"
                        />
                      </Form.Item>
                    </div>
                  </TabPane>

                  <TabPane tab="Заметки" key="notes">
                    <div style={{ paddingTop: 16 }}>
                      <Form.Item
                        name="notes"
                        label="Заметки"
                      >
                        <TextArea rows={3} placeholder="Заметки по продукту" />
                      </Form.Item>
                      <Form.Item
                        name="comment"
                        label="Комментарий"
                      >
                        <TextArea rows={3} placeholder="Комментарий" />
                      </Form.Item>
                    </div>
                  </TabPane>
                </Tabs>
              </Form>
            </Drawer>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default ProductPage;
